import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const Carousel = ({ items, CardComponent }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionDuration = 500;

  const updateItemsPerView = useCallback(() => {
    if (window.innerWidth < 768) {
      setItemsPerView(1);
    } else {
      setItemsPerView(3);
    }
    const maxPossibleIndex = Math.max(0, items.length - itemsPerView);
    setCurrentIndex((prevIndex) => Math.min(prevIndex, maxPossibleIndex));
  }, [items.length, itemsPerView]);

  useEffect(() => {
    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, [updateItemsPerView]);

  const totalSlides = Math.max(1, items.length - itemsPerView + 1);

  const goToNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
    setTimeout(() => setIsTransitioning(false), transitionDuration);
  };

  const goToPrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
    setTimeout(() => setIsTransitioning(false), transitionDuration);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), transitionDuration);
  };

  const itemWidthPercentage = 100 / itemsPerView;
  const translateXValue = -currentIndex * itemWidthPercentage;

  const showArrows = totalSlides > 1;

  return (
    <div className="w-full max-w-6xl mx-auto px-4 relative">
      <div className="overflow-hidden relative py-4">
        <div className={`flex transition-transform duration-${transitionDuration} ease-in-out`}
          style={{ transform: `translateX(${translateXValue}%)` }}
        >
          {items.map((item, index) => (
            <div
              key={item.id || index}
              className="flex-shrink-0 w-full md:w-1/3 px-2 pb-1"
            >
              <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 items-center ease-in-out p-5 md:p-6 h-40 flex flex-col justify-center">
                <h3 className="text-sm md:text-sm text-gray-800 mb-2 text-center">
                  {item.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>
      {showArrows && (
        <>
          <button
            onClick={goToPrev}
            disabled={isTransitioning}
            aria-label="Previous slide"
            className="absolute top-1/2 left-0 md:-left-4 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md z-10 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6 text-gray-700"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5 8.25 12l7.5-7.5"
              />
            </svg>
          </button>

          <button
            onClick={goToNext}
            disabled={isTransitioning}
            aria-label="Next slide"
            className="absolute top-1/2 right-0 md:-right-4 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md z-10 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6 text-gray-700"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </>
      )}

      {totalSlides > 1 && (
        <div className="flex justify-center space-x-2 mt-4">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              aria-label={`Go to slide ${index + 1}`}
              className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                currentIndex === index
                  ? "bg-gray-800"
                  : "bg-gray-300 hover:bg-gray-400"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

Carousel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  CardComponent: PropTypes.elementType,
};

export default Carousel;
